<template>
  <div>
    <b-container class="wide">
      <head-block :title="$t('title.customer.booking_requests')" />
      <booking-request-filter
        @filter-applied="updateBookingRequestWithFilter"/>
      <content-table
        head-variant="dark"
        :fields="fields"
        :items="bookings"
        :slots="templates"
      >
        <template
          slot="kind"
          slot-scope="data"
        >{{ getObjectByKeyValue(bookingTypes, 'code', data.tbl.item.kind).name }}</template>
        <template
          slot="type"
          slot-scope="data"
        ><span v-if="!data.tbl.item.type">-</span><span
          v-else-if="data.tbl.item.type === bookingRequestExtensionType && data.tbl.item.parentBookingRequest"
        >{{ `${getObjectByKeyValue(bookingRequestTypes, 'code', data.tbl.item.type).name} (#${data.tbl.item.parentBookingRequest})` }}</span><span
          v-else
        >{{ getObjectByKeyValue(bookingRequestTypes, 'code', data.tbl.item.type).name }}</span>
        </template>
        <template
          slot="created"
          slot-scope="data"
        >{{
          formatDate(data.tbl.item.created, userMarket?.supportCountry?.code, userMarket?.timezone)
        }}</template>
        <template
          slot="bookingPeriodName"
          slot-scope="data"
        >{{
          data.tbl.item.bookingPeriod ? bookingPeriodLabel(data.tbl.item.bookingPeriod) : $t('table.booking_periods.custom_period_label')
        }}</template>
        <template
          slot="shelfTypeName"
          slot-scope="data"
        >{{
          data.tbl.item.shelfName ? `${data.tbl.item.shelfTypeName} (${data.tbl.item.shelfName})` : data.tbl.item.shelfTypeName
        }}</template>
        <template
          slot="totalPrice"
          slot-scope="data"
        >{{ `${formatNumber(data.tbl.item.totalPrice, { precision: 2, marketCountry: userMarket?.supportCountry?.code })}${data.tbl.item.status === bookingRefunded && data.tbl.item.refundedGrossAmount ? ' (' + formatNumber(data.tbl.item.refundedGrossAmount, 2) + ')' : ''}`
          }}</template>
        <template slot="status" slot-scope="data">
          {{ data.tbl.item.kind === kindBookingRequest ? getBookingRequestStatus(data.tbl.item) : getObjectByKeyValue(bookingStatuses, 'code', parseInt(data.tbl.item.status)).name }}
        </template>
        <template
          slot="dateStart"
          slot-scope="data"
        >{{
          formatDate(data.tbl.item.dateStart, userMarket?.supportCountry?.code, userMarket?.timezone)
        }}</template>
        <template
          slot="dateEnd"
          slot-scope="data"
        >{{
          formatDate(data.tbl.item.dateEnd, userMarket?.supportCountry?.code, userMarket?.timezone)
        }}</template>
        <template slot="actions" slot-scope="data">
          <div class="d-flex justify-content-center">
            <b-button
              v-if="data.tbl.item.bookingAddonsData.length"
              size="sm"
              class="mr-1"
              v-b-tooltip.hover
              :title="$t('table.booking_requests.button_show_items_breakdown')"
              variant="secondary"
              @click="showBookingAddonsModal(data.tbl.item)">
              <b-icon icon="view-stacked"></b-icon>
            </b-button>
            <b-button
              v-if="canContinueCreateBookingRequest(data.tbl.item)"
              size="sm"
              class="mr-1"
              v-b-tooltip.hover
              :title="$t('pages.new_booking.go_to_checkout')"
              variant="warning"
              @click="$router.push({ name: 'create-booking' })">
              <b-icon icon="credit-card"></b-icon>
            </b-button>
            <b-button
              v-if="canExtendBookingRequest(data.tbl.item)"
              size="sm"
              class="mr-1"
              v-b-tooltip.hover
              :title="$t('table.booking_requests.button_extend_booking')"
              variant="primary"
              @click="$router.push({ name: 'extend-booking-request', params: { bookingRequestId: data.tbl.item.id }})">
              <b-icon icon="arrow-repeat"></b-icon>
            </b-button>
            <b-button
              v-if="canContinueBookingRequestExtension(data.tbl.item)"
              size="sm"
              class="mr-1"
              v-b-tooltip.hover
              :title="$t('table.booking_requests.button_pay_booking')"
              variant="info"
              @click="$router.push({ name: 'extend-booking-request', params: { bookingRequestId: data.tbl.item.id }})">
              <b-icon icon="cart-check"></b-icon>
            </b-button>
            <b-button
              v-if="canFinalizeBookingPaymentRequest(data.tbl.item)"
              size="sm"
              class="mr-1"
              v-b-tooltip.hover
              :title="$t('table.booking_requests.button_finalize_booking_payment_request')"
              variant="success"
              @click="$router.push({ name: 'booking-payment-request', params: { bookingRequestId: data.tbl.item.id }})">
              <b-icon icon="cash-coin"></b-icon>
            </b-button>
            <b-button
              v-if="canContinueFinalizeBookingPaymentRequest(data.tbl.item)"
              size="sm"
              class="mr-1"
              v-b-tooltip.hover
              :title="$t('table.booking_requests.button_pay_payment_request')"
              variant="info"
              @click="$router.push({ name: 'booking-payment-request', params: { bookingRequestId: data.tbl.item.id }})">
              <b-icon icon="cash-coin"></b-icon>
            </b-button>
          </div>
        </template>
      </content-table>
      <span v-if="!loader.isActive && !bookings.length" class="no-table-data">
        {{ $t('table.general.no_data') }}
      </span>
      <div
        v-if="pagesCount > 1"
        class="pagination_div">
        <paginate
          :value="page"
          :page-count="pagesCount"
          container-class="pagination-container"
          page-class="page-item"
          prev-class="btn btn-primary"
          next-class="btn btn-primary"
          active-class="active-class"
          :click-handler="clickPaginationCallback" />
      </div>
      <booking-addons-modal
        v-if="selectedObject"
        :selectedObject="selectedObject"
        :market="userMarket"
        :bookingAddonsData="selectedObject.bookingAddonsData"
        modalId="booking-addons-modal" />
    </b-container>
  </div>
</template>

<script>
  import {mapGetters, mapState} from 'vuex'
  import ContentTable from '@base/components/ContentTable.vue'
  import HeadBlock from '@base/components/HeadBlock.vue'
  import ObjectAction from '@base/mixins/ObjectAction.vue'
  import {
    BOOKING_CONFIRMED, BOOKING_EXPIRED,
    BOOKING_PAID, BOOKING_PAYMENT_INITIATED,
    BOOKING_REQUEST_TYPES,
    BOOKING_REQUEST_STATUSES, BOOKING_RESERVED, BOOKING_STATUSES, BOOKING_TYPES,
    TYPE_BOOKING, TYPE_BOOKING_REQUEST, BOOKING_REQUEST_EXTENSION_TYPE, BOOKING_REFUNDED,
  } from '@base/configs'
  import ObjectUtils from "@base/mixins/ObjectUtils.vue";
  import BookingRequestFilter from "@user/components/BookingRequestFilter.vue";
  import BookingAddonsModal from "@base/components/booking/BookingAddonsModal.vue";
  import Formatting from "@base/mixins/Formatting.vue";
  import BookingPeriodLabelMixin from "@base/mixins/BookingPeriodLabelMixin.vue";
  import {
    canContinueBookingRequestExtension, canContinueCreateBookingRequest, canContinueFinalizeBookingPaymentRequest,
    canExtendBookingRequest,
    canFinalizeBookingPaymentRequest
  } from "@base/utils";

  export default {
    name: 'BookingRequestView',

    components: {
      BookingAddonsModal,
      BookingRequestFilter,
      HeadBlock,
      ContentTable,
    },

    mixins: [ ObjectAction, ObjectUtils, Formatting, BookingPeriodLabelMixin ],

    data () {
      return {
        fields: [{ key: 'kind', label: this.$t('table.booking_requests.booking_type'), class: 'text-center'},
          { key: 'id', label: this.$t('table.booking_requests.id'), class: 'text-center'},
          { key: 'type', label: this.$t('table.booking_requests.type'), class: 'text-center'},
          { key: 'shelfTypeName', label: this.$t('table.booking_requests.shelf_type_name'), class: 'text-center'},
          { key: 'bookingPeriodName', label: this.$t('table.booking_requests.booking_period_name'), class: 'text-center'},
          { key: 'status', label: this.$t('table.booking_requests.status'), class: 'text-center'},
          { key: 'totalPrice', label: this.$t('table.booking_requests.discounted_price'), class: 'text-center'},
          { key: 'dateStart', label: this.$t('table.booking_requests.date_start'), class: 'text-center'},
          { key: 'dateEnd', label: this.$t('table.booking_requests.date_end'), class: 'text-center'},
          { key: 'created', label: this.$t('table.booking_requests.created'), class: 'text-center'},
          { key: 'actions', label: this.$t('form.general.actions'), class: 'text-center' }
        ],
        templates: [
          { name: 'kind', field: 'kind' },
          { name: 'type', field: 'type' },
          { name: 'dateStart', field: 'dateStart' },
          { name: 'dateEnd', field: 'dateEnd' },
          { name: 'shelfTypeName', field: 'shelfTypeName' },
          { name: 'bookingPeriodName', field: 'bookingPeriodName' },
          { name: 'totalPrice', field: 'totalPrice' },
          { name: 'created', field: 'created' },
          { name: 'status', field: 'status' },
          { name: 'actions', field: 'actions' },
        ],
        bookingRequestStatuses: BOOKING_REQUEST_STATUSES,
        bookingRequestTypes: BOOKING_REQUEST_TYPES,
        bookingExpired: BOOKING_EXPIRED,
        bookingReserved: BOOKING_RESERVED,
        bookingPaymentInitiated: BOOKING_PAYMENT_INITIATED,
        bookingPaid: BOOKING_PAID,
        bookingConfirmed: BOOKING_CONFIRMED,
        bookingRefunded: BOOKING_REFUNDED,
        bookingStatuses: BOOKING_STATUSES,
        bookingTypes: BOOKING_TYPES,
        kindBookingRequest: TYPE_BOOKING_REQUEST,
        kindBooking: TYPE_BOOKING,
        bookingRequestExtensionType: BOOKING_REQUEST_EXTENSION_TYPE,
        selectedObject: null,
        loader: { isActive: true },
        loading: false,
      }
    },

    computed: {
      ...mapState(['bookings', 'userMarket']),
      ...mapGetters({
        page: 'getBookingsPage',
        pagesCount: 'getBookingsPagesCount'
      }),
    },

    methods: {
      canContinueCreateBookingRequest,
      canContinueFinalizeBookingPaymentRequest,
      canFinalizeBookingPaymentRequest,
      canContinueBookingRequestExtension,
      canExtendBookingRequest,
      getAllBookings () {
        if (this.loading)
          return
        this.loading = true
        this.loader = this.$loading.show(this.$_loaderOptions)
        this.$store.commit(this.$_mutationTypes.SET_BOOKINGS_PAGE, 1)
        this.$store.dispatch(this.$_actionTypes.GET_ALL_CUSTOMER_BOOKINGS)
          .then((res) => {
            this.$store.commit(this.$_mutationTypes.SET_BOOKINGS_PAGES_COUNT, res.totalPages)
          })
          .catch((error) => {
            this.$_notifyError(error, this)
          })
          .finally(() => {
            this.loader.hide()
            this.loading = false
          })
      },

      clickPaginationCallback (pageNum) {
        if (this.loading)
          return
        this.loading = true
        this.loader = this.$loading.show(this.$_loaderOptions)

        this.$store.commit(this.$_mutationTypes.SET_BOOKINGS_PAGE, pageNum)
        this.$store.dispatch(this.$_actionTypes.GET_ALL_CUSTOMER_BOOKINGS)
          .then((res) => {
            this.$store.commit(this.$_mutationTypes.SET_BOOKINGS_PAGES_COUNT, res.totalPages)
          })
          .catch((error) => {
            this.$_notifyError(error, this)
          })
          .finally(() => {
            this.loader.hide()
            this.loading = false
          })
      },

      updateBookingRequestWithFilter () {
        this.$store.commit(this.$_mutationTypes.SET_BOOKINGS_PAGE, 1)
        this.getAllBookings()
      },

      showBookingAddonsModal (obj) {
        this.selectedObject = this.$_.cloneDeep(obj)
        this.$nextTick(() => {
          this.$bvModal.show("booking-addons-modal")
        })
      },

      getBookingRequestStatus (obj) {
        if (obj.isPaymentRequest && obj.status === BOOKING_RESERVED) {
          return this.$t('multiselect.single_label.booking_requests.payment_request')
        } else if (obj.isPaymentRequest && obj.status === BOOKING_PAYMENT_INITIATED) {
          return this.$t('multiselect.single_label.booking_requests.payment_request_payment_initiated')
        }
        return this.getObjectByKeyValue(this.bookingRequestStatuses, 'code', obj.status).name
      }
    }
  }
</script>

<style>

</style>
